import { Check, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  InputAdornment,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { useProgress } from "context/ProgressProvider";
import dayjs from "dayjs";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LoanService } from "services/LoanService";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
} from "shared/components/AppComponents";
import {
  BROKER_COMMISSION_RATE,
  LOAN_PURPOSE,
  LOANPAYBACK_OPTION,
} from "shared/helpers/Constants";
import {
  BrokerCommissionRateSchema,
  ExpectedLoanDateSchema,
  FundingTimeSchema,
  LoanAmountSchema,
  LoanPaybackSchema,
  LoanPurposeSchema,
  LoanTermSchema,
  PreferredRepaymentSchema,
} from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";

export const handleSaveLoan = (
  currentProfile,
  loanId,
  callback,
  formik = null,
  isBroker,
) => {
  LoanService.saveLoan(
    {
      loanAmount: currentProfile.loanAmount
        ? parseFloat(String(currentProfile.loanAmount).replace(/,/g, ""))
        : null,
      expectedLoanDate: currentProfile.expectedLoanDate,
      loanTerm: currentProfile.loanTerm,
      expectedTime: currentProfile.expectedTime,
      repaymentMethod: currentProfile.repaymentMethod,
      loanPurpose: currentProfile.loanPurpose,
      loanPayBackOption: currentProfile.loanPayBackOption,
      brokerCommissionRate: !isBroker ? 0 : currentProfile.brokerCommissionRate,
    },
    loanId,
  )
    .then((response: any) => {
      callback(true);
    })
    .catch((error: any) => {
      Utils.handleErrorResponse(error);
      callback(false);
    })
    .finally(() => {});
};

const YourLoan = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    currentProfile,
    isBroker,
  } = useProgress();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const [submitLoading, setSubmitLoading] = useState(false);
  const authInfo = useSelector((state: any) => state.authState);

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = isBroker ? 9 : 8;
  const PAGE_ID = "intro-loan";

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/sbl/${PAGE_ID}/1`, { replace: true });
    }
    console.log(currentStep);
  }, [currentStep, isValidStep, navigate]);

  const handleSubmitForm = (values: any, formik = null) => {
    if (formik) {
      setSubmitLoading(true);
    }
    console.log("formValues", values);
    dispatch(ProfileActions.setSblProfile(values));

    const nextStep = currentStep + 1;
    const jumpStep = currentStep + 2;

    if (nextStep < PAGE_LENGTH) {
      if (
        currentStep === 3 &&
        values.expectedTime !== "I have a specific date"
      ) {
        dispatch(
          ProfileActions.setSblProfile({
            expectedLoanDate: null,
          }),
        );
        navigateToSectionStepHandler(PAGE_ID, jumpStep);
        return;
      }
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    } else {
      if (currentProfile.isAllowedToEdit) {
        if (!currentProfile.isAllowedToEdit?.yourLoan) {
          handleCompleteAndNext();
          return;
        }
      }

      handleSaveLoan(
        {
          ...currentProfile,
          ...(!isBroker
            ? { loanPayBackOption: values.loanPayBackOption }
            : { brokerCommissionRate: values.brokerCommissionRate }),
        },
        currentProfile.loadId,
        (e) => {
          setSubmitLoading(false);
          if (e) {
            console.log(e);
            handleCompleteAndNext();
          }
        },
        formik,
        isBroker,
      );
    }
  };

  if (!isValidStep) {
    return null;
  }

  return (
    <>
      {currentStep === 1 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={LoanAmountSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              loanAmount: Utils.formatCurrency(currentProfile.loanAmount),
            }}
          >
            {({ handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Loan details" : "Your loan"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? "How much money your customer is looking to borrow?"
                    : "How much money are you looking to borrow?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Enter amount"
                  label=""
                  name="loanAmount"
                  outerLabel
                  className="ezifin-default-field"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  customIsoDollar
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 2 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={LoanTermSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              loanTerm:
                currentProfile.loanTerm === 0 ? null : currentProfile.loanTerm,
            }}
          >
            {({ handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">Loan term</Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? "What is their preferred loan term? (1-24 months)"
                    : "What is your preferred loan term? (1-24 months)"}
                </Typography>
                <AppFormikTextField
                  placeholder="Enter duration (Months)"
                  label=""
                  name="loanTerm"
                  outerLabel
                  className="ezifin-default-field"
                  type="number"
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 3 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={FundingTimeSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              expectedTime: currentProfile.expectedTime,
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors }) => {
              console.log(errors);
              const fundingTimeOptions = [
                "As soon as possible",
                "Within a week",
                "Within 2–3 weeks",
                "I have a specific date",
                "Just exploring options for now",
              ];
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "How soon do they need the funds?"
                      : "How soon do you need the funds?"}
                  </Typography>
                  {/* CHIPS */}
                  <Box mt={2}>
                    {fundingTimeOptions.map((option) => (
                      <Box mt={2} key={option}>
                        <Chip
                          label={option}
                          variant="outlined"
                          color={
                            values.expectedTime === option
                              ? "primary"
                              : "default"
                          }
                          icon={
                            values.expectedTime === option ? (
                              <Check fontSize="small" />
                            ) : (
                              <></>
                            )
                          }
                          onClick={() => setFieldValue("expectedTime", option)}
                          clickable
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      disabled={Object.keys(errors).length > 0}
                      onClick={handleSubmit}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 4 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={ExpectedLoanDateSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              expectedLoanDate: currentProfile.expectedLoanDate,
            }}
          >
            {({ handleSubmit, errors, values, setValues }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    Expected loan date?
                  </Typography>
                  <Box
                    mt={3}
                    sx={{
                      border: "1px solid #e7e7e7",
                      borderRadius: "6px",
                    }}
                  >
                    <DateCalendar
                      disabled={
                        currentProfile.isAllowedToEdit?.yourLoan === null
                          ? false
                          : currentProfile.isAllowedToEdit?.yourLoan ===
                              undefined
                            ? false
                            : !currentProfile.isAllowedToEdit?.yourLoan
                      }
                      minDate={dayjs()}
                      onChange={(date) => {
                        const formattedDate = dayjs(date).format("YYYY-MM-DD");
                        setValues({
                          expectedLoanDate: formattedDate,
                        });
                      }}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#2d68f7",
                              color: "white",
                            },
                          },
                        },
                      }}
                      sx={{
                        "&.MuiDateCalendar-root": {
                          maxWidth: "100%",
                        },
                      }}
                      {...(currentProfile.expectedLoanDate
                        ? { value: dayjs(values.expectedLoanDate) }
                        : {})}
                    />
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 5 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={PreferredRepaymentSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              repaymentMethod: currentProfile.repaymentMethod,
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors }) => {
              const preferredRepaymentOptions = [
                "Interest only (monthly in advance)",
                "Interest Capitalised",
              ];
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "What's their preferred repayment method?"
                      : "What's your preferred repayment method?"}
                  </Typography>
                  {/* CHIPS */}
                  <Box mt={2}>
                    {preferredRepaymentOptions.map((option) => (
                      <Box mt={2} key={option}>
                        <Chip
                          label={option}
                          variant="outlined"
                          color={
                            values.repaymentMethod === option
                              ? "primary"
                              : "default"
                          }
                          icon={
                            values.repaymentMethod === option ? (
                              <Check fontSize="small" />
                            ) : (
                              <></>
                            )
                          }
                          onClick={() =>
                            setFieldValue("repaymentMethod", option)
                          }
                          clickable
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 6 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={LoanPurposeSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              loanPurpose: currentProfile.loanPurpose,
            }}
          >
            {({ handleSubmit, errors }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    What is the Purpose of the loan? (Business Purposes only)
                  </Typography>

                  <Box mt={2}>
                    <FormControl sx={{ width: 325 }}>
                      <AppFormikSelect
                        placeholder="Select"
                        name="loanPurpose"
                        items={LOAN_PURPOSE}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                      />
                    </FormControl>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 7 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={LoanPaybackSchema}
            validateOnMount
            onSubmit={(formValues, formik) => {
              console.log(formValues, "LoanPaybackSchema");
              if (isBroker) {
                handleSubmitForm(formValues);
              } else {
                handleSubmitForm(formValues, formik);
              }
            }}
            initialValues={{
              loanPayBackOption: currentProfile.loanPayBackOption,
            }}
          >
            {({ handleSubmit, errors, isSubmitting }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Loan details" : "Your loan"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "How do they plan to pay back the loan?"
                      : "How do you plan to pay back the loan?"}
                  </Typography>

                  <Box mt={2}>
                    <FormControl sx={{ width: 325 }}>
                      <AppFormikSelect
                        placeholder="Select"
                        name="loanPayBackOption"
                        items={LOANPAYBACK_OPTION}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                      />
                    </FormControl>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={submitLoading}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0 || submitLoading}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {isBroker && currentStep === 8 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={BrokerCommissionRateSchema}
            validateOnMount
            onSubmit={(formValues, formik) => {
              handleSubmitForm(formValues, formik);
            }}
            initialValues={{
              brokerCommissionRate: currentProfile.brokerCommissionRate,
            }}
          >
            {({ handleSubmit, errors, isSubmitting, values }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">Loan details</Typography>
                  <Typography variant="h3" mt={3}>
                    Broker commission rate (ex. GST):
                  </Typography>

                  <Box mt={2}>
                    <FormControl sx={{ width: 325 }}>
                      <AppFormikSelect
                        placeholder="Select"
                        name="brokerCommissionRate"
                        items={BROKER_COMMISSION_RATE}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                      />
                    </FormControl>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={submitLoading}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={
                        values.brokerCommissionRate === 0 ||
                        Object.keys(errors).length > 0 ||
                        submitLoading
                      }
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}
    </>
  );
};

export default YourLoan;
